import type { FailureCause } from './failure';
import { classifyFailure } from './failure';

/**
 * 내부 진단을 쓰는 사람의 말로 옮긴다.
 *
 * 실패 메시지는 두 독자를 향한다. 하나는 재계획 프롬프트와 개발자 — 거기에는
 * `/w_tb_e050w`, `dataDetail`, `actcd`, 스킬 이름이 전부 있어야 한다. 다른 하나는
 * 사무직 직원이고, 그 사람에게 그것들은 아무 뜻이 없다. 지금까지 둘을 같은 문장으로
 * 냈다: `sales-by-business-site 스킬의 권장 화면 /w_tb_e050w을 쓰지 않았다`.
 *
 * 그래서 원문은 그대로 두고 **보여줄 때만** 이 함수를 거친다. 여기서 지켜야 할 것은
 * 세 가지다 — 무엇이 안 됐는지, 사용자 잘못인지 아닌지, 다음에 무엇을 하면 되는지.
 */

/** 사람에게 보여도 되는 문구인가. 이미 그렇게 쓰인 것은 건드리지 않는다. */
function alreadyPlain(message: string): boolean {
  return /입니다\.|습니다\.|주세요\.|주세요$/.test(message)
    && !/\/w_|rowsStateKey|requiredColumns|TaskSpec|스킬|__elman/.test(message);
}

/**
 * 원인마다 사용자에게 할 말.
 *
 * 무엇이 실패했는지 가리는 일은 `failure.ts` 가 한다. 여기서 정규식을 또 쓰면 규칙이
 * 두 벌이 되고, 실제로 그렇게 어긋난 적이 있다 — 값이 비었다는 말이 "화면을 찾지
 * 못했다" 로 나갔다.
 */
const SAID: Record<FailureCause, string> = {
  'screen-not-found': '요청하신 내용을 낼 수 있는 화면을 찾지 못했습니다. 기간이나 조건을 조금 바꿔서 다시 물어봐 주세요.',
  'wrong-screen': '요청하신 항목이 그 화면에 없습니다. 다른 기준으로 다시 물어봐 주세요.',
  'empty-result': '조건에 맞는 자료가 없습니다. 기간이나 조건을 바꿔서 다시 물어봐 주세요.',
  'empty-column': '조건에 맞는 자료가 없습니다. 기간이나 조건을 바꿔서 다시 물어봐 주세요.',
  'useless-result': '자료는 찾았지만 값이 없어 계산하지 못했습니다. 기간을 바꿔서 다시 물어봐 주세요.',
  'blocked-by-notice': '이 화면에서는 그 작업을 할 수 없습니다. 화면에 뜬 안내를 확인해 주세요.',
  'option-not-found': '화면에서 값을 고르지 못했습니다. 정확한 이름을 알려 주시면 다시 해보겠습니다.',
  'draft-partial': '초안을 다 채우지 못했습니다. 화면에서 남은 칸을 확인해 주세요.',
  'upstream-down': 'AI 서비스에 연결하지 못했습니다. 잠시 뒤 다시 시도해 주세요.',
  // **「자료가 없습니다」라고 말하면 안 되는 자리다.** 화면이 서버 응답을 못 받으면
  // 표가 빈 채로 남는데, 그것을 결과로 읽으면 없는 사실을 단언하게 된다.
  'screen-no-answer': '화면이 서버에서 자료를 받지 못했습니다. 자료가 없는 것인지는 확인하지 못했습니다. 잠시 뒤 다시 물어봐 주세요.',
  unknown: '요청을 처리하지 못했습니다. 다시 한 번 물어봐 주세요.',
};

/** 사용량 한도는 장애가 아니다. 기다린다고 풀리지 않으니 따로 말한다. */
function quotaMessage(raw: string): string | null {
  return /HTTP 429|daily AI call limit/.test(raw)
    ? '오늘 사용할 수 있는 AI 요청을 다 썼습니다. 내일 다시 이용해 주세요.'
    : null;
}

/**
 * 화면 안내는 살리고 껍데기만 걷는다.
 *
 * 자재청구서에서 나간 문장이 이랬다 — `신규를 시작하지 못했다: 확인!: 견적없는
 * 자재청구는 신규 할 수 없습니다!\r 견적서에서 자재를…`. 뒷부분은 **사용자가 알아야
 * 할 회사 규칙**이라 지우면 안 된다. 앞의 `신규를 시작하지 못했다` 는 드라이버가
 * 재계획에게 하는 말이고, `확인!:` 과 `\r` 은 대화상자의 껍데기다.
 */
function polish(message: string): string {
  return message
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f]+/g, ' ')
    // 화면 안내에는 **글자 그대로의** `\r` 이 들어 있다 — 제어문자가 아니라 역슬래시와
    // r 두 글자다. 자재청구서 안내가 그랬고, 제어문자만 걷었더니 그대로 나갔다.
    .replace(/\\[rn]/g, ' ')
    .replace(/^신규를 시작하지 못했다:?\s*/, '이 화면에서는 새로 작성을 시작할 수 없습니다. 화면 안내: ')
    .replace(/(^|[\s:])확인!?\s*:\s*/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * 화면에 띄울 한 문장.
 *
 * 원문은 부르는 쪽이 로그에 남긴다. 여기서는 돌려주지 않는다 — 붙여 보내면 결국
 * 사용자가 읽게 되고, 그러면 옮긴 뜻이 없다.
 */
export function userMessage(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error ?? '');
  if (!raw.trim()) return SAID.unknown;
  const quota = quotaMessage(raw);
  if (quota) return quota;
  if (alreadyPlain(raw)) return polish(raw);
  const cause = classifyFailure(error);
  // **화면이 무엇을 달라는지는 사용자만 채울 수 있다.** `화면에 뜬 안내를 확인해 주세요`
  // 는 화면을 보고 있지 않은 사람에게 아무 말도 아니다 — 재고를 물었을 때 화면은
  // `먼저 창고를 선택해주세요` 라고 했는데 그 말이 사용자에게 닿지 않았다.
  if (cause === 'blocked-by-notice') {
    const said = /차단됐다:\s*(.+)$/.exec(raw)?.[1];
    if (said) return `${SAID['blocked-by-notice']} 화면 안내: ${polish(said)}`;
  }
  return SAID[cause];
}

/** 이 문장으로 끝난 턴은 실패한 턴이다. 다음 발화가 그것을 가리킬 수 있다. */
export function looksLikeFailure(answer: string): boolean {
  const text = answer.trim();
  if (!text) return false;
  return Object.values(SAID).some((said) => text.includes(said))
    || text.includes('사용할 수 있는 AI 요청을 다 썼습니다');
}
